import { useState } from 'react'
import { mutate } from '@/runtime'
import type { Resource } from '@/db/types'
import { KindError } from '@/kinds/kind'
import { useProjects } from '@/db/hooks'
import { Button, Field, Sheet, Tag, cx } from '@/components/ui'
import { stateTone } from '@/lib/colour'
import type { GitIssueSpec, GitIssueStatus, PullRequestSpec, PullRequestStatus } from './schema'
import type { GitIssueResource, PullRequestResource } from './apply'

const providerIcon: Record<string, string> = { forgejo: '🐙', github: '🐈' }

const act = async (id: string, action: string, args?: unknown, onError?: (m: string) => void) => {
  try {
    await mutate.action(id, action, args)
  } catch (e) {
    onError?.(e instanceof KindError ? e.message : (e as Error).message)
  }
}

const Pending = ({ status }: { status: { writebacks?: unknown[]; writeback_error?: string } }) => {
  if (status.writeback_error) return <Tag tone="warn" title={status.writeback_error}>sync failed</Tag>
  if (status.writebacks?.length) return <Tag>sending…</Tag>
  return null
}

// ---- rows -------------------------------------------------------------------

export const IssueListRow = ({ resource, onOpen }: { resource: Resource<GitIssueSpec, GitIssueStatus>; onOpen?: (r: GitIssueResource) => void }) => (
  <li>
    <button type="button" onClick={() => onOpen?.(resource)} className="card w-full text-left px-3 py-2.5 min-h-12" data-mirror-id={resource.id}>
      <div className={cx('leading-5', resource.status.state === 'closed' && 'text-faint line-through')}>
        <span className="mr-1.5">{providerIcon[resource.spec.source.provider] ?? '•'}</span>
        {resource.spec.title}
      </div>
      <div className="flex flex-wrap gap-1 mt-0.5">
        <Tag>
          {resource.spec.repo}#{resource.spec.number}
        </Tag>
        {resource.spec.labels.map((l) => (
          <Tag key={l}>{l}</Tag>
        ))}
        {resource.spec.assignees.length > 0 && <Tag>→ {resource.spec.assignees.join(', ')}</Tag>}
        <Pending status={resource.status} />
      </div>
    </button>
  </li>
)

export const PullListRow = ({ resource, onOpen }: { resource: Resource<PullRequestSpec, PullRequestStatus>; onOpen?: (r: PullRequestResource) => void }) => {
  const s = resource.spec
  const st = resource.status
  return (
    <li>
      <button type="button" onClick={() => onOpen?.(resource)} className="card w-full text-left px-3 py-2.5 min-h-12" data-mirror-id={resource.id}>
        <div className={cx('leading-5', st.state !== 'open' && 'text-faint')}>
          <span className="mr-1.5">🔀</span>
          {s.draft && <span className="text-faint mr-1">draft</span>}
          {s.title}
        </div>
        <div className="flex flex-wrap gap-1 mt-0.5">
          <Tag>
            {s.repo}#{s.number}
          </Tag>
          <Tag>{s.author}</Tag>
          {s.review_requested && !st.review_state && <Tag tone="accent">review requested</Tag>}
          {st.review_state && <Tag tone={st.review_state === 'approved' ? 'done' : 'warn'}>{st.review_state.replace('_', ' ')}</Tag>}
          {st.state === 'merged' && <Tag tone="done">merged</Tag>}
          {st.state === 'closed' && <Tag tone={stateTone('someday')}>closed</Tag>}
          {s.checks !== 'unknown' && <Tag tone={s.checks === 'success' ? 'done' : s.checks === 'failure' ? 'warn' : 'muted'}>checks {s.checks}</Tag>}
          {s.mergeable === false && <Tag tone="warn">conflicts</Tag>}
          <Pending status={st} />
        </div>
      </button>
    </li>
  )
}

// ---- detail sheets ------------------------------------------------------------

const Body = ({ text }: { text: string }) =>
  text ? <p className="text-sm text-fg-2 whitespace-pre-wrap leading-6 max-h-60 overflow-y-auto mb-3">{text}</p> : null

const CommentBox = ({ onSend, label = 'Comment' }: { onSend: (body: string) => Promise<void>; label?: string }) => {
  const [text, setText] = useState('')
  return (
    <div className="flex gap-2 items-start mb-3">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} className="flex-1 text-sm" placeholder={`${label}…`} />
      <Button
        onClick={async () => {
          if (!text.trim()) return
          await onSend(text.trim())
          setText('')
        }}
        disabled={!text.trim()}
      >
        Send
      </Button>
    </div>
  )
}

export const IssueDetail = ({ resource: r }: { resource: Resource<GitIssueSpec, GitIssueStatus> }) => {
  const [err, setErr] = useState<string | null>(null)
  const linkTask = async () => {
    const t = await mutate.create('Task', { title: r.spec.title, icon: '🐛', notes: r.spec.source.url }, { ...r.labels })
    await mutate.setEdge(t.id, r.id, 'references')
    await act(r.id, 'link_task', { task_id: t.id }, setErr)
  }
  return (
    <article>
      <div className="flex flex-wrap gap-1 mb-2">
        <Tag>
          {r.spec.repo}#{r.spec.number}
        </Tag>
        <Tag tone={r.status.state === 'open' ? 'accent' : 'muted'}>{r.status.state}</Tag>
        <Tag>by {r.spec.author}</Tag>
        {r.spec.milestone && <Tag>{r.spec.milestone}</Tag>}
        <Pending status={r.status} />
      </div>
      <Body text={r.spec.body} />
      <CommentBox onSend={(body) => act(r.id, 'comment', { body }, setErr)} />
      <div className="flex flex-wrap gap-1.5">
        {r.status.state === 'open' ? <Button variant="chip" onClick={() => void act(r.id, 'close', {}, setErr)}>Close</Button> : <Button variant="chip" onClick={() => void act(r.id, 'reopen', {}, setErr)}>Reopen</Button>}
        {!r.status.task_id && <Button variant="chip" onClick={() => void linkTask()}>Make a task</Button>}
        <a href={r.spec.source.url} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full bg-panel border border-line px-3 min-h-8 text-xs text-fg-2">
          Open on {r.spec.source.provider} ↗
        </a>
      </div>
      {err && <p className="text-xs text-rose-300 mt-2">{err}</p>}
    </article>
  )
}

export const PullDetail = ({ resource: r }: { resource: Resource<PullRequestSpec, PullRequestStatus> }) => {
  const [err, setErr] = useState<string | null>(null)
  const [changes, setChanges] = useState(false)
  const open = r.status.state === 'open'
  return (
    <article>
      <div className="flex flex-wrap gap-1 mb-2">
        <Tag>
          {r.spec.repo}#{r.spec.number}
        </Tag>
        <Tag tone={open ? 'accent' : r.status.state === 'merged' ? 'done' : 'muted'}>{r.status.state}</Tag>
        <Tag>by {r.spec.author}</Tag>
        <Tag>
          {r.spec.head} → {r.spec.base}
        </Tag>
        {r.status.review_state && <Tag tone={r.status.review_state === 'approved' ? 'done' : 'warn'}>{r.status.review_state.replace('_', ' ')}</Tag>}
        <Pending status={r.status} />
      </div>
      <Body text={r.spec.body} />
      {open && !changes && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Button big variant="primary" onClick={() => void act(r.id, 'approve', { body: '' }, setErr)} data-testid="pr-approve">
            Approve
          </Button>
          <Button big onClick={() => setChanges(true)}>
            Request changes
          </Button>
        </div>
      )}
      {open && changes && <CommentBox label="What needs to change" onSend={async (body) => { await act(r.id, 'request_changes', { body }, setErr); setChanges(false) }} />}
      <CommentBox onSend={(body) => act(r.id, 'comment', { body }, setErr)} />
      <div className="flex flex-wrap gap-1.5">
        {open && r.spec.mergeable !== false && <Button variant="chip" onClick={() => void act(r.id, 'merge', {}, setErr)}>Merge</Button>}
        {open && <Button variant="chip" onClick={() => void act(r.id, 'close', {}, setErr)}>Close</Button>}
        {r.status.state === 'closed' && <Button variant="chip" onClick={() => void act(r.id, 'reopen', {}, setErr)}>Reopen</Button>}
        <a href={r.spec.source.url} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full bg-panel border border-line px-3 min-h-8 text-xs text-fg-2">
          Open on {r.spec.source.provider} ↗
        </a>
      </div>
      {err && <p className="text-xs text-rose-300 mt-2">{err}</p>}
    </article>
  )
}

/** A sheet wrapper the views use to open a mirror. */
export const MirrorSheet = ({ resource, onClose }: { resource: GitIssueResource | PullRequestResource | null; onClose: () => void }) => {
  const projects = useProjects()
  void projects
  if (!resource) return null
  return (
    <Sheet open onClose={onClose} title={resource.name}>
      {resource.kind === 'PullRequest' ? <PullDetail resource={resource as PullRequestResource} /> : <IssueDetail resource={resource as GitIssueResource} />}
    </Sheet>
  )
}

export const FieldNote = Field
